import { ReactNode, useEffect, useRef } from "react";
import moment from "moment";
import FlexRow from "../../../../../components/base/FlexRow";
import Icon from "../../../../../components/base/Icon";
import { FontAwesomeIcons } from "../../../../../core/Types";
import { clearToolbar, setPage, setToolbarActions, useScreen } from "../../../../../core/Global";
import WalletDateFilter, { WalletDateRange } from "../WalletDateFilter";


export const money = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" })

export const num = (v?: number) => (v ?? 0).toLocaleString("en-US")

// Seconds as "3h 25m", "12m 05s" or "40s".
export function fmtDuration(seconds?: number) {
    const s = Math.max(0, Math.round(seconds ?? 0))
    const h = Math.floor(s / 3600)
    const m = Math.floor((s % 3600) / 60)
    const sec = s % 60
    if (h > 0) return `${h}h ${m}m`
    if (m > 0) return `${m}m ${String(sec).padStart(2, "0")}s`
    return `${sec}s`
}

// Minutes as "3h 25m" or "25m".
export function fmtMinutes(minutes?: number) {
    const m = Math.max(0, Math.round(minutes ?? 0))
    const h = Math.floor(m / 60)
    return h > 0 ? `${num(h)}h ${m % 60}m` : `${m}m`
}

export const pct = (part: number, total: number) => total > 0 ? Math.round(part / total * 1000) / 10 : 0


// Billing period of the account's plan, as the backend returns it with each report.
export type UsagePeriod = {
    planName?: string
    start: string
    end: string
    included: number
    used: number
    overageRate: number
}


// Puts the page title and the period filter (current month by default) in the page header.
// On mobile the header bar is hidden, so the filter is returned to render inline instead.
export function useUsageHeader(title: string, elementId: string, onChange: (range: WalletDateRange) => void) {

    const screen = useScreen()

    useEffect(() => {
        setPage(title, "Usages", "System")
        if (screen.isMobile) return
        setToolbarActions(() => <WalletDateFilter onChange={onChange} storageKey={null} defaultId="cm" elementId={elementId} />)
        return () => { clearToolbar() }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [screen.isMobile])

    if (!screen.isMobile) return null
    return (
        <div className="mb-5">
            <WalletDateFilter onChange={onChange} storageKey={null} defaultId="cm" elementId={elementId} />
        </div>
    )
}


// Rounded badge with a duotone icon, used as each card's corner accent.
export function UsageCardIcon({ icon, color, brands }: { icon: FontAwesomeIcons; color: string; brands?: boolean }) {
    return (
        <div className="symbol symbol-45px">
            <span className={`symbol-label bg-light-${color}`}>
                {
                    brands ?
                        <Icon icon={icon} type="brands" size={22} className={`text-${color}`} />
                        :
                        <Icon icon={icon} doutone size={22} className={`text-${color}`} />
                }
            </span>
        </div>
    )
}


type StatCardProps = {
    title: string
    value: ReactNode
    caption?: ReactNode
    icon: FontAwesomeIcons
    color: string
    brands?: boolean
    children?: ReactNode
}

// A KPI card: title + icon, a big value, a caption, then optional detail rows.
export function UsageStatCard({ title, value, caption, icon, color, brands, children }: StatCardProps) {
    return (
        <div className="card h-100">
            <div className="card-body">
                <FlexRow justify="between" className="mb-4">
                    <span className="text-gray-600 fw-semibold fs-6">{title}</span>
                    <UsageCardIcon icon={icon} color={color} brands={brands} />
                </FlexRow>

                <div className="fs-2hx fw-bold text-dark lh-1">{value}</div>
                {caption && <span className="text-gray-400 fs-8">{caption}</span>}

                {
                    children &&
                    <>
                        <div className="separator my-4" />
                        <div className="d-flex flex-column gap-2">{children}</div>
                    </>
                }
            </div>
        </div>
    )
}


// One "label ......... value" line inside a card.
export function UsageRow({ label, value, dot }: { label: ReactNode; value: ReactNode; dot?: string }) {
    return (
        <FlexRow justify="between" className="fs-7">
            <FlexRow>
                {
                    dot &&
                    <span className="d-inline-block rounded-circle me-2"
                        style={{ width: 9, height: 9, backgroundColor: `var(${dot})` }} />
                }
                <span className="text-gray-600">{label}</span>
            </FlexRow>
            <span className="text-dark fw-semibold">{value}</span>
        </FlexRow>
    )
}


// Thin progress bar; turns warning past 80% and danger at 100%, like the plan warnings.
export function UsageProgress({ used, total }: { used: number; total: number }) {
    const p = total > 0 ? Math.min(100, used / total * 100) : 0
    const color = p >= 100 ? "danger" : p >= 80 ? "warning" : "primary"
    return (
        <div className="h-8px w-100 bg-light rounded">
            <div className={`bg-${color} rounded h-8px`} style={{ width: `${p}%` }} />
        </div>
    )
}


type AllowanceProps = {
    unit: string
    fromAllowance: number
    fromWallet: number
    walletAmount: number
    period?: UsagePeriod | null
    rangeLabel?: string
    formatValue?: (v: number) => string
}

// Where the selected range's usage was paid from (plan allowance vs wallet), next to the live
// state of the current billing period's allowance.
export function UsageAllowanceCards({ unit, fromAllowance, fromWallet, walletAmount, period, rangeLabel, formatValue }: AllowanceProps) {

    const f = formatValue ?? ((v: number) => `${num(v)} ${unit}`)
    const total = fromAllowance + fromWallet
    const left = period ? Math.max(0, period.included - period.used) : 0

    return (
        <div className="row g-5 mb-5">

            <div className="col-md-6">
                <div className="card h-100">
                    <div className="card-body">
                        <FlexRow justify="between" className="mb-4">
                            <span className="text-gray-600 fw-semibold fs-6">Allowance vs Wallet</span>
                            <UsageCardIcon icon="scale-balanced" color="primary" />
                        </FlexRow>

                        <div className="text-gray-400 fs-8 mb-4">{rangeLabel ? `In ${rangeLabel}` : "In selected period"}</div>

                        {/* Two-tone bar: allowance share then wallet share. */}
                        <div className="d-flex h-10px w-100 rounded overflow-hidden bg-light mb-5">
                            <div className="bg-primary" style={{ width: `${pct(fromAllowance, total)}%` }} />
                            <div className="bg-warning" style={{ width: `${pct(fromWallet, total)}%` }} />
                        </div>

                        <div className="d-flex flex-column gap-3">
                            <UsageRow dot="--bs-primary" label="From plan allowance" value={f(fromAllowance)} />
                            <UsageRow dot="--bs-warning" label="From wallet"
                                value={<>{f(fromWallet)} <span className="text-gray-500 fw-normal ms-1">({money.format(walletAmount)})</span></>} />
                        </div>
                    </div>
                </div>
            </div>

            <div className="col-md-6">
                <div className="card h-100">
                    <div className="card-body">
                        <FlexRow justify="between" className="mb-4">
                            <span className="text-gray-600 fw-semibold fs-6">Current Billing Period</span>
                            <UsageCardIcon icon="badge-check" color="success" />
                        </FlexRow>
                        {
                            !period ?
                                <div className="text-gray-500 fs-7">
                                    This account is not on a plan with an included allowance, so all usage is charged to the wallet.
                                </div>
                                :
                                <>
                                    <div className="text-gray-400 fs-8 mb-4">
                                        {period.planName} · {moment(period.start).format("MMM D")} – {moment(period.end).format("MMM D, YYYY")}
                                    </div>

                                    <FlexRow justify="between" className="mb-2">
                                        <span className="fs-4 fw-bold text-dark">{f(period.used)}</span>
                                        <span className="text-gray-500 fs-7">of {f(period.included)}</span>
                                    </FlexRow>
                                    <UsageProgress used={period.used} total={period.included} />

                                    <div className="d-flex flex-column gap-3 mt-5">
                                        <UsageRow label="Remaining" value={f(left)} />
                                        <UsageRow label="Overage rate" value={`${money.format(period.overageRate)} / ${unit.replace(/s$/, "")}`} />
                                    </div>
                                </>
                        }
                    </div>
                </div>
            </div>

        </div>
    )
}


export type ChartSeries = { name: string; data: number[]; color: string; type?: "bar" | "line" }

type ChartProps = {
    title: string
    subtitle?: string
    days: string[]
    series: ChartSeries[]
    stacked?: boolean
    valueFormatter?: (v: number) => string
}

// Daily chart (ApexCharts, as the wallet chart): stacked bars, optionally with a line series.
export function UsageDailyChart({ title, subtitle, days, series, stacked, valueFormatter }: ChartProps) {

    const ref = useRef<HTMLDivElement>(null)
    const chartRef = useRef<any>(null)

    useEffect(() => {
        if (!ref.current) return
        const Apex = (window as any).ApexCharts
        const KTUtil = (window as any).KTUtil
        if (!Apex || !KTUtil) return

        const labelColor = KTUtil.getCssVariableValue("--bs-gray-500")
        const borderColor = KTUtil.getCssVariableValue("--bs-gray-200")
        const hasLine = series.some(s => s.type === "line")

        const options = {
            series: series.map(s => ({ name: s.name, type: s.type ?? "bar", data: s.data })),
            chart: {
                fontFamily: "inherit",
                type: "bar",
                height: 320,
                stacked: !!stacked,
                toolbar: { show: false },
                zoom: { enabled: false }
            },
            plotOptions: { bar: { columnWidth: days.length > 20 ? "70%" : "45%", borderRadius: 3 } },
            legend: { show: true, position: "top", horizontalAlign: "right", labels: { colors: labelColor } },
            dataLabels: { enabled: false },
            stroke: { width: series.map(s => s.type === "line" ? 3 : 0), curve: "smooth" },
            colors: series.map(s => KTUtil.getCssVariableValue(s.color)),
            xaxis: {
                categories: days.map(d => moment(d).format("MMM D")),
                axisBorder: { show: false },
                axisTicks: { show: false },
                tickAmount: Math.min(days.length, 15),
                labels: { rotate: 0, hideOverlappingLabels: true, style: { colors: labelColor, fontSize: "12px" } }
            },
            yaxis: {
                labels: {
                    style: { colors: labelColor, fontSize: "12px" },
                    formatter: (v: number) => valueFormatter ? valueFormatter(v) : num(Math.round(v))
                }
            },
            tooltip: {
                shared: true,
                intersect: false,
                y: { formatter: (v: number) => valueFormatter ? valueFormatter(v) : num(v) }
            },
            grid: { borderColor, strokeDashArray: 4, yaxis: { lines: { show: true } } },
            markers: { size: hasLine ? 3 : 0 }
        }

        if (chartRef.current) { try { chartRef.current.destroy() } catch { } }
        chartRef.current = new Apex(ref.current, options)
        chartRef.current.render()

        return () => {
            if (chartRef.current) { try { chartRef.current.destroy() } catch { }; chartRef.current = null }
        }
    }, [days, series, stacked])

    return (
        <div className="card">
            <div className="card-header border-0 pt-6">
                <div>
                    <h3 className="card-title fw-bold m-0">{title}</h3>
                    {subtitle && <div className="text-gray-500 fs-7 mt-1">{subtitle}</div>}
                </div>
            </div>
            <div className="card-body pt-3">
                <div ref={ref} style={{ minHeight: 320 }} />
            </div>
        </div>
    )
}
